import {createContext, FC, PropsWithChildren, useCallback, useContext, useRef} from "react";
import {SnackbarKey} from "notistack";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

interface BulkActionSnackbarContextValue {
	/** Store a processing snackbar ID so it can be closed when the WS event arrives. */
	setSnackbarId: (key: string, id: SnackbarKey) => void;
	/** Close a previously stored processing snackbar and remove its reference. */
	closeSnackbar: (key: string) => void;
}

const BulkActionSnackbarContext = createContext<BulkActionSnackbarContextValue | undefined>(undefined);

export const BulkActionSnackbarProvider: FC<PropsWithChildren> = ({children}) => {
	const snackbarIds = useRef<Map<string, SnackbarKey>>(new Map());
	const {closeSnackbar} = useCustomSnackbar();

	const setSnackbarId = useCallback((key: string, id: SnackbarKey) => {
		snackbarIds.current.set(key, id);
	}, []);

	const closeTrackedSnackbar = useCallback((key: string) => {
		const id = snackbarIds.current.get(key);
		if (id != null) {
			closeSnackbar(id);
			snackbarIds.current.delete(key);
		}
	}, [closeSnackbar]);

	return (
		<BulkActionSnackbarContext.Provider value={{setSnackbarId, closeSnackbar: closeTrackedSnackbar}}>
			{children}
		</BulkActionSnackbarContext.Provider>
	);
};

export const useBulkActionSnackbar = (): BulkActionSnackbarContextValue => {
	const context = useContext(BulkActionSnackbarContext);
	if (!context) {
		throw new Error("useBulkActionSnackbar must be used within a BulkActionSnackbarProvider");
	}
	return context;
};
